
import React from "react";
import { i18nManager } from "../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { CacheManager, Debugger, Schemes, Utils } from "../../../utils";
import { BoolHelper, Button, ButtonManageRef, Checkbox, Classname, FileHelper, FileInput, FileInputItemOptions, FileInputManageRef, FileInputMode, ObjectHelper, Orientation, Popover, PopoverManageRef, TextAreaInput } from "@ronuse/noseur";
import { ApiResponseData, IPlatform, IProfile, ISupportRequestComment, ISupportRequest, SanitizedResponse, SupportRequestService, SupportRequestCommentService, IAttachmentType, ConsusBaseService, IStatus } from "../../../services";

export type TicketProps = {
    setTitle: (title: string) => void;
    setSubTitle: (title: string) => void;
    setShowBreadCrumb: (show: boolean) => void;
    setOnTokenRefreshed: (fn: () => void) => void;
};

function Ticket(props: TicketProps) {
    const { id } = ReactRouterDom.useParams();
    const { platform } = ReactRouterDom.useParams();
    const consusService = ConsusBaseService.getInstance();
    const labels = i18nManager.Labels.support.ticket.ticket;
    const [comment, setComment] = React.useState<string>("");
    const activePlatform = CacheManager.getPlatform(platform);
    const commentTextAreaContainerRef = React.useRef<any>(null);
    const sendButtonManageRef = React.useRef<ButtonManageRef>(null);
    const [isPreview, setIsPreview] = React.useState<boolean>(false);
    const fileInputManageRef = React.useRef<FileInputManageRef>(null);
    const supportRequestService = SupportRequestService.getInstance();
    const attachPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const profile = CacheManager.get(CacheManager.PROFILE_KEY) as IProfile;
    const [supportRequest, setSupportRequest] = React.useState<ISupportRequest>();
    const supportRequestCommentService = SupportRequestCommentService.getInstance();
    const [beforeFetchContent, setBeforeFetchContent] = React.useState<boolean>(false);
    const [supportRequestComments, setSupportRequestComments] = React.useState<ApiResponseData<ISupportRequestComment>>({});

    React.useEffect(() => {
        props.setShowBreadCrumb(false);
        props.setOnTokenRefreshed(fetchTicketDetails);
        fetchTicketDetails();
    }, []);

    if (!activePlatform) {
        return Utils.LOADING_STATE;
    }

    return (<div className="view-ticket">
        <div className="participants">
            {supportRequest?.participants?.map((participant) => (<div key={participant.external_id} className={Classname.build({ "active": participant.external_id === profile.external_id })}>
                <img alt={participant.first_name} src={participant.profile_picture_location} />
                <span className="lined-limit">{participant.first_name} {participant.last_name}</span>
            </div>))}
        </div>
        <div className="support-comments">
            <div className="comments">
                {supportRequestComments.content?.map((comment, index) => (<div key={comment.external_id} className={Classname.build("comment", { "c-highlight": comment.user.external_id === profile.external_id })} ref={(r) => {
                    if (r && beforeFetchContent && (index + 1) === ((supportRequestComments.content?.length ?? 0))) {
                        setTimeout(() => r?.scrollIntoView(), 300);
                        setBeforeFetchContent(false);
                    }
                }}>
                    <div className="user">
                        <img alt={comment.user.first_name} src={comment.user.profile_picture_location} />
                        <div className="details">
                            <span className="name">{comment.user.first_name} {comment.user.last_name}</span>
                            <span className="date">{Utils.formatToFineDate(comment.created_at)}</span>
                        </div>
                    </div>
                    <div className="content">
                        <div className="text" dangerouslySetInnerHTML={{ __html: Utils.MARKDOWN_CONVERTER.makeHtml(comment.comment.replace(/\n/g, "<br />")) }} />
                        {comment.attachments?.length > 0 && (<div className="attachments">
                            <div className="title">
                                <i className="fa fa-paperclip" />
                                <span>{comment.attachments.length} {labels.attachments}</span>
                            </div>
                            <div className="items attachment-items">
                                {comment.attachments.map((attachment, index) => (<a key={"attachment-" + index} className="item" href={attachment.location} rel="noreferrer" target="_blank">
                                    <i className={Classname.build("fa", {
                                        "fa-music": (attachment.type === IAttachmentType.AUDIO),
                                        "fa-image": (attachment.type === IAttachmentType.IMAGE),
                                        "fa-video": (attachment.type === IAttachmentType.VIDEO),
                                        "fa-file": (attachment.type === IAttachmentType.UNKNOWN),
                                    })} />
                                    <div>
                                        <span className="name">{attachment.name}</span>
                                        <span className="size">{attachment.size}</span>
                                    </div>
                                </a>))}
                            </div>
                        </div>)}
                    </div>
                </div>))}
            </div>
            {!BoolHelper.equalsAny(supportRequest?.status, [IStatus.CLOSED, IStatus.RESOLVED, IStatus.UNRESOLVED]) && (<div className="message-box">
                {!isPreview && (<TextAreaInput ref={commentTextAreaContainerRef} className="comment-area" scheme={Schemes.RIVTN_HERA} placeholder={labels.controls.reply}
                    defaultValue={comment} onInputComplete={setComment} borderless />)}
                {isPreview && (<div className="comment-area" dangerouslySetInnerHTML={{ __html: Utils.MARKDOWN_CONVERTER.makeHtml(comment) }} />)}
                <div className="bottom">
                    <Checkbox scheme={Schemes.RIVTN_HERA} label={labels.controls.send_ticket_thread} />
                    <div className="controls">
                        <Button scheme={Schemes.RIVTN_HERA} onClick={() => setIsPreview(!isPreview)} text={isPreview ? labels.controls.edit : labels.controls.preview} textOnly fillOnHover />
                        <Button scheme={Schemes.RIVTN_HERA} text={labels.controls.attach} leftIcon="fa fa-paperclip"
                            onClick={(e: any) => attachPopOverManageRef.current?.toggle(e)} outlined fillOnHover />
                        <Button scheme={Schemes.RIVTN_HERA} text={labels.controls.send} manageRef={sendButtonManageRef} loadingProps={Utils.BUTTON_LOADING_PROPS} onClick={sendComment}
                            outlined fillOnHover />
                    </div>
                </div>
                <FileInput dragAndDropRefs={[commentTextAreaContainerRef]} orientation={Orientation.VERTICAL} mode={FileInputMode.PREVIEW}
                    onDragComplete={() => commentTextAreaContainerRef.current.style.borderColor = "transparent"} emptyTemplate={() => null}
                    manageRef={fileInputManageRef} onDragBegin={() => commentTextAreaContainerRef.current.style.borderColor = "#0FA883"}
                    maxFileSize={2000000} itemTemplate={(options: FileInputItemOptions) => {
                        const fileKey = `![${options.file.name}-${options.index}]`;
                        if (options.file.type.includes("image")) {
                            if (!comment.includes(fileKey)) {
                                const fileMarkdownString = `${fileKey}(${URL.createObjectURL(options.file)})\n`;
                                const cursorPosition = commentTextAreaContainerRef.current?.selectionStart;
                                let updatedComment = Utils.stringSplice(comment, cursorPosition, fileMarkdownString);
                                commentTextAreaContainerRef.current.value = updatedComment;
                                setComment(updatedComment);
                            }
                        }
                        return (<div key={"attachment-" + options.file.name} className="item">
                            <i className={Classname.build("fa", {
                                "fa-image": (options.file.type.includes("image")),
                                "fa-music": (options.file.type.includes("audio")),
                                "fa-video": (options.file.type.includes("video")),
                                "fa-file": !(options.file.type.includes("image") || options.file.type.includes("audio") || options.file.type.includes("video")),
                            })} />
                            <div>
                                <span className="name">{options.file.name}</span>
                                <span className="size">{options.formattedSize}</span>
                            </div>
                            <i className="fa fa-times" onClick={() => {
                                if (options.file.type.includes("image")) {
                                    const fileKeyIndex = comment.indexOf(fileKey);
                                    const filePreviewDeletionFrom = fileKeyIndex;
                                    const filePreviewDeletionTo = comment.indexOf(")", fileKeyIndex + 1) + 2;
                                    let updatedComment = Utils.stringCleave(comment, filePreviewDeletionFrom, filePreviewDeletionTo);
                                    commentTextAreaContainerRef.current.value = updatedComment;
                                    setComment(updatedComment);
                                }
                                options.onRemove(options.file);
                            }} />
                        </div>);
                    }} onValidationFail={(file: File, error: string) => {
                        Utils.reportError(error);
                        return true;
                    }}
                    noDragAndDrop concatNewFile multiple />
            </div>)}
        </div>

        <Popover manageRef={attachPopOverManageRef} className="popover" dismissOnClick>
            <span style={{ justifyContent: "flex-start" }} onClick={() => fileInputManageRef.current?.select()}><i className="fa fa-file" style={{ marginRight: 20 }} />{labels.controls.documents}</span>
            <span style={{ justifyContent: "flex-start" }} onClick={() => fileInputManageRef.current?.select()}><i className="fa fa-image" style={{ marginRight: 20 }} />{labels.controls.medias}</span>
        </Popover>
    </div>);

    function fetchTicketDetails() {
        props.setShowBreadCrumb(false);
        supportRequestService.getSingleSupportRequest(id!).then(({ sanitized }: SanitizedResponse<any>) => {
            Debugger.log("Ticket:fetchTicketDetails:getSingleSupportRequest", sanitized);
            setSupportRequest(sanitized);
            props.setTitle((sanitized as ISupportRequest).title);
            props.setSubTitle(`${SupportRequestService.getStatusLabel(sanitized.status)} - ${Utils.formatToFineDate(sanitized.created_at)}`);
        }).catch((error: SanitizedResponse<any>) => {
            Utils.reportError(error);
        });
        supportRequestCommentService.querySupportRequestComments({ sort: "id,ASC", "support_request.external_id": id!, size: 400 }).then(({ sanitized }: SanitizedResponse<any>) => {
            Debugger.log("Ticket:fetchTicketDetails:querySupportRequestComments", sanitized);
            setSupportRequestComments(sanitized);
        }).catch((error: SanitizedResponse<any>) => {
            Utils.reportError(error);
        });
    }

    async function sendComment() {
        let newComment = comment;
        const payload: any = {
            attachments: [],
            support_request_id: id,
            platform_id: activePlatform?.platform_id,
            user: {
                last_name: profile.last_name,
                first_name: profile.first_name,
                middle_name: profile.other_names,
                profile_picture_location: profile.profile_picture_location
            }
        };
        if (!comment && !fileInputManageRef.current?.files().length) {
            Utils.reportError(labels.missing_comment_attachment);
            return;
        }
        const successfulUploads: string[] = [];
        sendButtonManageRef.current?.setLoadingState(true);
        try {
            for (let index = 0; index < fileInputManageRef.current!.files().length; index++) {
                const file = fileInputManageRef.current!.files()[index];
                let type = "UNKNOWN";
                if (file.type.includes("audio")) type = "AUDIO";
                if (file.type.includes("video")) type = "VIDEO";
                if (file.type.includes("image")) type = "IMAGE";
                const fileKey = `![${file.name}-${index}]`;
                const uploadResult = (await consusService.uploadFile(id!, file)).data?.data;
                successfulUploads.push(uploadResult.relative_path);
                if (type == "IMAGE") {
                    const fileKeyIndex = newComment.indexOf(fileKey);
                    const filePreviewDeletionFrom = fileKeyIndex + fileKey.length + 1;
                    const filePreviewDeletionTo = newComment.indexOf(")", fileKeyIndex + 1);
                    newComment = Utils.stringSplice(newComment, filePreviewDeletionFrom, uploadResult.consus_download_url, filePreviewDeletionTo);
                } else {
                    payload.attachments.push({
                        type,
                        name: file.name,
                        location: uploadResult.consus_download_url,
                        size: FileHelper.humanFileSize(file.size),
                    });
                }
            }
            payload.comment = newComment;
            const commentResult = (await supportRequestCommentService.createSupportTicketComment(payload)).data.data;
            Debugger.log("Ticket:sendComment:createSupportTicketComment:success", commentResult);
            setComment("");
            fileInputManageRef.current?.clear();
            commentTextAreaContainerRef.current.value = "";
            setBeforeFetchContent(true);
            fetchTicketDetails();
        } catch (error: any) {
            Utils.reportError(error);
            Debugger.error("Ticket:sendComment:error", "Sending comment failed", error);
            if (successfulUploads.length) {
                consusService.deleteUploadedFiles(successfulUploads).catch(Utils.reportError);
            }
        }
        sendButtonManageRef.current?.setLoadingState(false);
    }

}

export default Ticket;
